import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';

import Iconify from '@/components/media/iconify';
import { useOdooPush } from '@/hooks/integrations/use-odoo-push';
import type { OdooResourceType, CreateInOdooResult } from '@/types/odoo.types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface OdooPushDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Close handler. */
  onClose: () => void;
  /** The entity type to push. */
  resourceType: OdooResourceType;
  /** The Hub resource MongoDB ID. */
  resourceId: string;
  /** Display name of the Hub resource. */
  resourceName: string;
  /** Summary data of the Hub resource to show as preview. */
  resourceData?: Record<string, unknown>;
  /** Called after a successful push. */
  onSuccess?: (result: CreateInOdooResult) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Readable label for a resource type. */
const RESOURCE_TYPE_LABELS: Record<OdooResourceType, string> = {
  organizations: 'Organization',
  'legal-entities': 'Legal Entity',
  contacts: 'Contact',
  plans: 'Plan',
};

/** Fields to display in the preview depending on resource type. */
const PREVIEW_FIELDS: Record<OdooResourceType, string[]> = {
  organizations: ['name', 'country', 'city', 'website'],
  'legal-entities': ['name', 'taxNumber', 'country'],
  contacts: ['name', 'email', 'phone'],
  plans: ['name', 'sku', 'listPrice'],
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OdooPushDialog({
  open,
  onClose,
  resourceType,
  resourceId,
  resourceName,
  resourceData,
  onSuccess,
}: OdooPushDialogProps) {
  const { t } = useTranslation();

  const { push, isLoading } = useOdooPush({
    resourceType,
    onSuccess: (result) => {
      onSuccess?.(result);
      // Force-close without checking isLoading — mutation already succeeded
      onClose();
    },
  });

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handlePush = () => {
    push(resourceId);
  };

  const typeLabel = t(RESOURCE_TYPE_LABELS[resourceType]);
  const previewFields = PREVIEW_FIELDS[resourceType];

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Iconify icon="mdi:cloud-upload" width={24} />
        {t('Create in Odoo')}
      </DialogTitle>

      <DialogContent>
        {/* Hub resource info */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('Hub Resource')}
          </Typography>
          <Typography variant="body1">
            <strong>{typeLabel}:</strong> {resourceName}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Preview of data that will be sent */}
        {resourceData && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('Data that will be sent to Odoo')}
            </Typography>
            {previewFields.map((field) => {
              const value = resourceData[field];
              if (value === undefined || value === null || value === '') return null;
              return (
                <Typography key={field} variant="body2" sx={{ mb: 0.5 }}>
                  <strong>{t(field)}:</strong> {String(value)}
                </Typography>
              );
            })}
          </Box>
        )}

        {/* Warning */}
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t(
            'This action will create a new record in Odoo with the data from this Hub resource. The Odoo ID will be automatically linked to this resource.'
          )}
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={handleClose} disabled={isLoading}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handlePush}
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <Iconify icon="mdi:cloud-upload" width={18} />
            )
          }
        >
          {isLoading ? t('Creating...') : t('Create in Odoo')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
